import { create } from 'zustand';
import { Reference } from '../features/chat/types/conversation';

/**
 * Thought Process Store
 *
 * Manages the state for the thought process panel that shows the reasoning
 * behind assistant responses. The panel opens when users click "Show Thought Process"
 * in the assistant message actions menu.
 */

// Thought process data interface
export interface ThoughtProcessData {
  thoughtprocess: string;
  datapoints: Reference[];
  agent_classification: string[];
}

export enum PanelTab {
  ThoughtProcess = 'thoughtProcess',
  SourceParagraphs = 'sourceParagraphs',
}

// Store state interface
interface ThoughtProcessState {
  conversationID: string;
  messageID: string;
  // Panel visibility state
  isOpen: boolean;
  panelTab: PanelTab;

  // Current thought process data being displayed
  currentThoughtProcess: ThoughtProcessData;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setTab: (tab: PanelTab) => void;

  // Data management
  setConversationID: (id: string) => void;
  setMessageID: (id: string) => void;
  setThoughtProcess: (data: ThoughtProcessData) => void;
  clearThoughtProcess: () => void;

  // Error handling
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useThoughtProcessStore = create<ThoughtProcessState>((set, get) => ({
  // Initial state
  conversationID: '',
  messageID: '',
  isOpen: false,
  panelTab: PanelTab.ThoughtProcess,
  currentThoughtProcess: {thoughtprocess: '', datapoints: [], agent_classification: []},
  isLoading: true,
  error: null,

  // Panel actions
  openPanel: () => {
    set({
      isOpen: true,
      error: null
    });
  },

  closePanel: () => {
    set({
      isOpen: false,
      error: null
    });
  },

  togglePanel: () => {
    const { isOpen } = get();
    set({ isOpen: !isOpen });
  },

  setTab: (tab: PanelTab) => {
    set({
      panelTab: tab,
      error: null
    });
  },

  // Data management
  setConversationID: (id: string) => {
    set({ conversationID: id });
  },

  setMessageID: (id: string) => {
    set({ messageID: id });
  },

  setThoughtProcess: (data: ThoughtProcessData) => {
    set({
      currentThoughtProcess: data,
      error: null
    });
  },

  clearThoughtProcess: () => {
    set({
      currentThoughtProcess: {thoughtprocess: '', datapoints: [], agent_classification: []},
      error: null
    });
  },

  // Error handling
  setError: (error: string | null) => {
    set({ error, isLoading: false });
  },

  setLoading: (loading: boolean) => {
    set({
      isLoading: loading,
      error: null,
    });
  },
}));

/**
 * Utility selector hooks for thought process state
 * These are hooks that should be called inside React components
 */
export const useThoughtProcessSelectors = {
  // Panel state selectors
  useIsOpen: () => useThoughtProcessStore(state => state.isOpen),
  useHasThoughtProcess: () => useThoughtProcessStore(state =>
    !!state.currentThoughtProcess && (
      state.currentThoughtProcess.thoughtprocess !== '' ||
      (state.currentThoughtProcess.datapoints && state.currentThoughtProcess.datapoints.length > 0) ||
      (state.currentThoughtProcess.agent_classification && state.currentThoughtProcess.agent_classification.length > 0)
    )
  ),
  useIsLoading: () => useThoughtProcessStore(state => state.isLoading),
  useHasError: () => useThoughtProcessStore(state => state.error !== null),

  // Data selectors
  useCurrentThoughtProcess: () => useThoughtProcessStore(state =>
    state.currentThoughtProcess
  ),
  useCurrentMessageID: () => useThoughtProcessStore(state =>
    state.messageID
  ),
  useCurrentConversationID: () => useThoughtProcessStore(state =>
    state.conversationID
  ),
  // Thought Process Citations - New Functionality (Phase 2)
  //useCitations: () => useThoughtProcessStore(state =>
  //  state.currentThoughtProcess?.citations || []
  //),
  useError: () => useThoughtProcessStore(state => state.error),
};

// Export types
export type { ThoughtProcessState };
